import {
	faEnvelope,
	faMobileScreen,
	faSpinner,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { formatContactPhoneForDisplay } from '@supply-mover-2-0/core';
import type {
	PortalContactKind,
	PortalProfileDto,
} from '@supply-mover-2-0/shared';
import {
	ConfirmDialog,
	updateStoredBiometricLabel,
	useTranslation,
} from '@supply-mover-2-0/web-ui';
import type { FormEvent, JSX } from 'react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MobilePageHeader } from '../components/layout/MobilePageHeader';
import { useTenant } from '../components/TenantGate';
import { usePortalAuth } from '../hooks/usePortalAuth';
import { maskPhoneNumber } from '../lib/maskPhone';
import {
	confirmContactChange,
	removeContact,
	requestContactChange,
} from '../services/portalAccountService';
import { useLoginModalStore } from '../stores/loginModalStore';
import { usePortalAuthStore } from '../stores/portalAuthStore';

/** How long before another code may be asked for (the server allows one a minute). */
const RESEND_COOLDOWN_SECONDS = 60;

/** Where one contact detail is in its change. */
type EditStageModel =
	| { stage: 'enter'; kind: PortalContactKind }
	| {
			stage: 'code';
			kind: PortalContactKind;
			/** The value the code went to, exactly as the server stored it. */
			value: string;
	  };

const INPUT_CLASS =
	'mt-1 block w-full rounded-md border border-brand-border bg-brand-input px-3 py-2 text-sm text-default focus:outline-none focus:ring-2 focus:ring-portal-accent';
const PRIMARY_BUTTON_CLASS =
	'rounded-md bg-portal-accent enabled:hover:bg-portal-accent-hover disabled:opacity-50 disabled:cursor-not-allowed text-white font-medium px-4 py-2 text-sm transition-colors';
const SECONDARY_BUTTON_CLASS =
	'rounded-md border border-brand-border px-3 py-2 text-sm text-strong hover:bg-brand-input disabled:opacity-50 disabled:cursor-not-allowed';

const hasValue = (value: string | null | undefined): value is string =>
	value != null && value.trim() !== '';

/**
 * The label the biometric quick sign-in shows for this account. Mirrors the
 * Account page's enrollment label (email, else masked phone, else name), so an
 * enrollment made there can be found and relabeled after a change.
 */
function biometricLabelFor(profile: PortalProfileDto | null): string {
	if (profile == null) {
		return '';
	}
	if (hasValue(profile.email)) {
		return profile.email;
	}
	if (hasValue(profile.phoneNumber)) {
		return maskPhoneNumber(profile.phoneNumber);
	}
	return `${profile.firstName ?? ''} ${profile.lastName ?? ''}`.trim();
}

function errorMessageOf(error: unknown, fallback: string): string {
	return error instanceof Error && error.message !== ''
		? error.message
		: fallback;
}

/**
 * PD-16726 — Account → Email & Phone. A storefront user changes, adds or
 * removes the email address and phone number they sign in with.
 *
 * A new value only takes effect after the 6-digit code sent TO IT is entered,
 * which proves the person can receive mail or texts there. A value can be
 * removed while the other one remains; with the storefront's phone sign-in off
 * the email address cannot be removed, because a phone number is then not a
 * sign-in name. The old address or number is told about every change.
 *
 * Other devices stay signed in, as with Change Password. A new phone number
 * starts without consent to texts, so the success message points to
 * Notifications.
 */
export function ContactDetailsPage(): JSX.Element {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { token, isAuthenticated, profile } = usePortalAuth();
	const { authOptions } = useTenant();
	const openLoginModal = useLoginModalStore((s) => s.open);

	const [edit, setEdit] = useState<EditStageModel | null>(null);
	const [valueInput, setValueInput] = useState('');
	const [codeInput, setCodeInput] = useState('');
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [success, setSuccess] = useState<{
		kind: PortalContactKind;
		removed: boolean;
	} | null>(null);
	const [confirmRemove, setConfirmRemove] = useState<PortalContactKind | null>(
		null,
	);
	const [resendSeconds, setResendSeconds] = useState(0);

	// Signed-out visitors get the login modal over an empty page, like the
	// other account pages.
	useEffect(() => {
		if (!isAuthenticated) {
			openLoginModal();
		}
	}, [isAuthenticated, openLoginModal]);

	// Count down the resend cooldown once a code has gone out.
	useEffect(() => {
		if (resendSeconds <= 0) {
			return;
		}
		const timer = setTimeout(() => {
			setResendSeconds((seconds) => Math.max(0, seconds - 1));
		}, 1000);
		return (): void => {
			clearTimeout(timer);
		};
	}, [resendSeconds]);

	if (!isAuthenticated || profile == null) {
		return <div />;
	}

	const email = hasValue(profile.email) ? profile.email : null;
	const phone = hasValue(profile.phoneNumber) ? profile.phoneNumber : null;

	const label = (kind: PortalContactKind): string =>
		kind === 'email' ? 'email address' : 'phone number';

	const startEdit = (kind: PortalContactKind): void => {
		setEdit({ stage: 'enter', kind });
		setValueInput('');
		setCodeInput('');
		setError(null);
		setSuccess(null);
	};

	const cancelEdit = (): void => {
		setEdit(null);
		setValueInput('');
		setCodeInput('');
		setError(null);
	};

	/** The profile changed: refresh it everywhere and relabel biometric sign-in. */
	const applyProfile = async (next: PortalProfileDto): Promise<void> => {
		const before = biometricLabelFor(profile);
		usePortalAuthStore.getState().setProfile(next);
		await updateStoredBiometricLabel({
			from: before,
			to: biometricLabelFor(next),
		});
	};

	const sendCode = async (
		kind: PortalContactKind,
		rawValue: string,
	): Promise<void> => {
		if (token == null) {
			return;
		}
		setBusy(true);
		setError(null);
		try {
			const result = await requestContactChange(token, {
				kind,
				value: rawValue,
			});
			setEdit({ stage: 'code', kind, value: result.value });
			setCodeInput('');
			setResendSeconds(RESEND_COOLDOWN_SECONDS);
		} catch (requestError) {
			setError(
				errorMessageOf(
					requestError,
					'We could not send a code right now. Please try again.',
				),
			);
		} finally {
			setBusy(false);
		}
	};

	const handleSendCode = (event: FormEvent): void => {
		event.preventDefault();
		if (edit == null || valueInput.trim() === '') {
			return;
		}
		void sendCode(edit.kind, valueInput.trim());
	};

	const handleConfirm = async (event: FormEvent): Promise<void> => {
		event.preventDefault();
		if (token == null || edit?.stage !== 'code') {
			return;
		}
		setBusy(true);
		setError(null);
		try {
			const next = await confirmContactChange(token, {
				kind: edit.kind,
				value: edit.value,
				code: codeInput.trim(),
			});
			await applyProfile(next);
			setSuccess({ kind: edit.kind, removed: false });
			setEdit(null);
		} catch (confirmError) {
			setError(
				errorMessageOf(
					confirmError,
					'We could not confirm the code. Please try again.',
				),
			);
		} finally {
			setBusy(false);
		}
	};

	const handleRemove = async (kind: PortalContactKind): Promise<void> => {
		setConfirmRemove(null);
		if (token == null) {
			return;
		}
		setBusy(true);
		setError(null);
		setSuccess(null);
		try {
			const next = await removeContact(token, { kind });
			await applyProfile(next);
			setSuccess({ kind, removed: true });
		} catch (removeError) {
			setError(
				errorMessageOf(
					removeError,
					'We could not update your account right now. Please try again.',
				),
			);
		} finally {
			setBusy(false);
		}
	};

	/**
	 * Remove is offered while the other value remains, and never for the email
	 * while phone sign-in is off (the server refuses both anyway).
	 */
	const canRemove = (kind: PortalContactKind): boolean =>
		kind === 'email'
			? email != null && phone != null && authOptions.phoneLoginEnabled
			: phone != null && email != null;

	const renderEditor = (kind: PortalContactKind): JSX.Element | null => {
		if (edit?.kind !== kind) {
			return null;
		}

		if (edit.stage === 'enter') {
			return (
				<form onSubmit={handleSendCode} className="mt-4 space-y-3">
					<label className="block">
						<span className="text-sm font-medium text-strong">
							{kind === 'email' ? t('New email address') : t('New phone number')}
						</span>
						<input
							type={kind === 'email' ? 'email' : 'tel'}
							inputMode={kind === 'email' ? 'email' : 'tel'}
							autoComplete={kind === 'email' ? 'email' : 'tel'}
							value={valueInput}
							onChange={(e) => setValueInput(e.target.value)}
							className={INPUT_CLASS}
							placeholder={
								kind === 'email' ? t('name@example.com') : t('(555) 123-4567')
							}
							disabled={busy}
							required
						/>
					</label>
					<p className="text-xs text-brand-muted">
						{kind === 'email'
							? t('We will email a 6-digit code to the new address to confirm it.')
							: t('We will text a 6-digit code to the new number to confirm it.')}
					</p>
					<div className="flex flex-wrap gap-2">
						<button
							type="submit"
							disabled={busy || valueInput.trim() === ''}
							className={PRIMARY_BUTTON_CLASS}
						>
							{busy ? t('Sending...') : t('Send Code')}
						</button>
						<button
							type="button"
							onClick={cancelEdit}
							disabled={busy}
							className={SECONDARY_BUTTON_CLASS}
						>
							{t('Cancel')}
						</button>
					</div>
				</form>
			);
		}

		const shownValue =
			kind === 'phone'
				? (formatContactPhoneForDisplay(edit.value) ?? edit.value)
				: edit.value;

		return (
			<form
				onSubmit={(event) => {
					void handleConfirm(event);
				}}
				className="mt-4 space-y-3"
			>
				<p className="text-sm text-default">
					{kind === 'email'
						? t('We emailed a 6-digit code to')
						: t('We texted a 6-digit code to')}{' '}
					<span className="font-medium">{t(shownValue)}</span>
				</p>
				<label className="block">
					<span className="text-sm font-medium text-strong">
						{t('Verification code')}
					</span>
					<input
						type="text"
						inputMode="numeric"
						autoComplete="one-time-code"
						pattern="[0-9]*"
						maxLength={6}
						value={codeInput}
						onChange={(e) =>
							setCodeInput(e.target.value.replace(/\D/g, '').slice(0, 6))
						}
						className={`${INPUT_CLASS} tracking-[0.5em] text-lg font-semibold`}
						disabled={busy}
						aria-label={t('Verification code')}
						required
					/>
				</label>
				<div className="flex flex-wrap gap-2">
					<button
						type="submit"
						disabled={busy || codeInput.length !== 6}
						className={PRIMARY_BUTTON_CLASS}
					>
						{busy ? t('Saving...') : t('Confirm')}
					</button>
					<button
						type="button"
						onClick={() => {
							void sendCode(kind, edit.value);
						}}
						disabled={busy || resendSeconds > 0}
						className={SECONDARY_BUTTON_CLASS}
					>
						{resendSeconds > 0
							? t('Send a new code in {{seconds}}s', {
									seconds: resendSeconds,
								})
							: t('Send a new code')}
					</button>
					<button
						type="button"
						onClick={cancelEdit}
						disabled={busy}
						className={SECONDARY_BUTTON_CLASS}
					>
						{t('Cancel')}
					</button>
				</div>
				<p className="text-xs text-brand-muted">
					{t('The code expires in 15 minutes.')}
				</p>
			</form>
		);
	};

	const renderRow = (kind: PortalContactKind): JSX.Element => {
		const current = kind === 'email' ? email : phone;
		const shown =
			current == null
				? null
				: kind === 'phone'
					? (formatContactPhoneForDisplay(current) ?? current)
					: current;
		const isEditing = edit?.kind === kind;

		return (
			<div className="p-4">
				<div className="flex items-center gap-3">
					<FontAwesomeIcon
						icon={kind === 'email' ? faEnvelope : faMobileScreen}
						className="w-5 text-brand-muted"
					/>
					<div className="min-w-0 flex-1">
						<div className="text-xs text-brand-muted">
							{kind === 'email' ? t('Email address') : t('Phone number')}
						</div>
						<div className="text-sm text-default truncate">
							{shown != null
								? t(shown)
								: kind === 'email'
									? t('No email address')
									: t('No phone number')}
						</div>
					</div>
					{!isEditing && (
						<div className="flex items-center gap-2 shrink-0">
							<button
								type="button"
								onClick={() => startEdit(kind)}
								disabled={busy || edit != null}
								className={SECONDARY_BUTTON_CLASS}
							>
								{current != null ? t('Change') : t('Add')}
							</button>
							{canRemove(kind) && (
								<button
									type="button"
									onClick={() => {
										setError(null);
										setSuccess(null);
										setConfirmRemove(kind);
									}}
									disabled={busy || edit != null}
									className="rounded-md px-3 py-2 text-sm text-danger hover:bg-danger-tint disabled:opacity-50 disabled:cursor-not-allowed"
								>
									{t('Remove')}
								</button>
							)}
						</div>
					)}
				</div>
				{renderEditor(kind)}
			</div>
		);
	};

	return (
		<div className="px-4 py-4 max-w-2xl mx-auto">
			<MobilePageHeader title="Email & Phone" backPath="/account" />
			<h1 className="hidden md:block text-lg md:text-xl font-bold text-default mb-1">
				{t('Email & Phone')}
			</h1>
			<p className="text-sm text-brand-muted mb-4">
				{authOptions.phoneLoginEnabled
					? t('You sign in with your email address or your phone number.')
					: t('You sign in with your email address.')}
			</p>

			<div className="space-y-4">
				{success != null && (
					<div className="rounded-md px-3 py-3 text-sm bg-success-badge text-success-tint-fg border border-success-tint space-y-2">
						<p>
							{success.removed
								? success.kind === 'email'
									? t('Your email address was removed.')
									: t('Your phone number was removed.')
								: success.kind === 'email'
									? t(
											'Your email address was updated. Use it the next time you sign in.',
										)
									: t(
											'Your phone number was updated. Use it the next time you sign in.',
										)}
						</p>
						{success.kind === 'phone' && !success.removed && (
							<p>
								{t(
									'Texts about your account need your OK for the new number.',
								)}{' '}
								<button
									type="button"
									onClick={() => {
										void navigate('/account/notifications');
									}}
									className="underline font-medium"
								>
									{t('Turn on texts in Notifications')}
								</button>
							</p>
						)}
					</div>
				)}

				{error != null && (
					<div className="rounded-md px-3 py-2 text-sm bg-danger-badge text-danger-tint-fg border border-danger-tint">
						{t(error)}
					</div>
				)}

				<div className="bg-brand-surface border border-brand-border rounded-xl overflow-hidden divide-y divide-subtle">
					{renderRow('email')}
					{renderRow('phone')}
				</div>

				{busy && edit == null && (
					<div className="flex justify-center">
						<FontAwesomeIcon
							icon={faSpinner}
							className="animate-spin text-brand-muted"
						/>
					</div>
				)}
			</div>

			<ConfirmDialog
				isOpen={confirmRemove != null}
				variant="danger"
				title={
					confirmRemove === 'email'
						? t('Remove your email address?')
						: t('Remove your phone number?')
				}
				message={
					confirmRemove == null
						? ''
						: t(
								'You will no longer be able to sign in with this {{label}}, and account messages will no longer go to it. We will let it know it was removed.',
								{ label: t(label(confirmRemove)) },
							)
				}
				confirmText={t('Remove')}
				cancelText={t('Cancel')}
				isProcessing={busy}
				onConfirm={() => {
					if (confirmRemove != null) {
						void handleRemove(confirmRemove);
					}
				}}
				onCancel={() => {
					setConfirmRemove(null);
				}}
			/>
		</div>
	);
}
